import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { useEffect, useState } from 'react';
import { ResourceTemplateFormValues } from '../resource-templates-form-page';
import UserAvatar from '../../../../shared/user-avatar';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Delete from '@mui/icons-material/Delete';
import Group from '@mui/icons-material/Group';

interface StepTemplateSharesProps {
  control: Control<ResourceTemplateFormValues>;
  setValue: UseFormSetValue<ResourceTemplateFormValues>;
  watch: UseFormWatch<ResourceTemplateFormValues>;
}

interface ShareObject {
  [key: string]: string;
}

export const roleOptions = [
  {
    displayValue: 'See Only Free/Busy',
    value: 'freeBusyReader',
  },
  {
    displayValue: 'See All Events Details',
    value: 'reader',
  },
];

export default function StepTemplateShares({
  control,
  setValue,
  watch,
}: StepTemplateSharesProps) {
  const [selectedShareType, setSelectedShareType] = useState('');
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');

  const calSharedToDomain = watch('calSharedToDomain');
  const calPublic = watch('calPublic');
  const shares = watch('shares');

  function addSelection(email: string) {
    setValue('shares', {
      ...shares,
      [`${selectedShareType}:${email}`]: 'reader',
    });
    setAutoCompleteResponse('');
    setSelectedShareType('');
  }

  useEffect(() => {
    const key = `${selectedShareType}:${autoCompleteResponse}`;

    if (
      selectedShareType &&
      autoCompleteResponse &&
      !Object.prototype.hasOwnProperty.call(shares, key)
    ) {
      addSelection(autoCompleteResponse);
    }
  }, [selectedShareType, autoCompleteResponse, shares, addSelection]);

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Grid sx={{ pt: 1, ml: 2 }} container spacing={2} size={{ xs: 10 }}>
        <Grid size={{ xs: 4 }}>
          <Controller
            name="calSharedToDomain"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.checked);
                      if (!e.target.checked && calSharedToDomain) {
                        setValue('calSharedToDomain', !calSharedToDomain);
                      }
                    }}
                  />
                }
                label="Calendar shared to domain"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Controller
            name="domainRole"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Domain Role"
                disabled={!calSharedToDomain}
                sx={{ width: '100%' }}
                value={field.value || ''}
                onChange={field.onChange}
              >
                {roleOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Controller
            name="calPublic"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.checked);
                      if (e.target.checked && !calPublic) {
                        setValue('calPublic', !calPublic);
                      }
                    }}
                  />
                }
                label="Calendar is public"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Controller
            name="publicRole"
            control={control}
            render={({ field }) => (
              <TextField
                select
                disabled={!calPublic}
                label="Public Role"
                sx={{ width: '100%' }}
                value={field.value || ''}
                onChange={field.onChange}
              >
                {roleOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
      </Grid>
      <Stack flexDirection="row" gap={1} sx={{ my: 3 }}>
        <Box sx={{ width: 400 }}>
          <SharedAutocomplete
            sources={['users', 'groups']}
            autocompleteValue={autoCompleteResponse}
            setAutoCompleteValue={setAutoCompleteResponse}
            setAutoCompleteTypeValue={setSelectedShareType}
          />
        </Box>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <Paper
          sx={{
            minWidth: 630,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
          }}
        >
          <TableContainer
            sx={{ width: '80%', flex: 1, minHeight: 0, maxHeight: '100%' }}
          >
            <Table size={'small'} stickyHeader aria-label="searched-shares">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shares
                  ? Object.keys(shares).map((user) => {
                      const selectedShare = user.split(':')[1];
                      const selectedShareType = user.split(':')[0];
                      return (
                        <TableRow key={selectedShare}>
                          <TableCell>
                            <Stack
                              flexDirection="row"
                              alignItems="center"
                              gap={2}
                            >
                              {selectedShareType === 'user' ? (
                                <UserAvatar userEmail={selectedShare} />
                              ) : (
                                <Avatar children={<Group />} />
                              )}
                              <Typography variant="body2">
                                {selectedShare}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="space-between"
                              gap={1}
                            >
                              <TextField
                                inputProps={{
                                  id: 'role',
                                }}
                                InputLabelProps={{
                                  htmlFor: 'role',
                                }}
                                select
                                label="Role"
                                size="small"
                                value={shares[user as keyof object]}
                                onChange={(event) => {
                                  const copy: ShareObject = { ...shares };
                                  copy[user] = event.target.value;
                                  setValue('shares', copy);
                                }}
                                sx={{ width: 140 }}
                              >
                                <MenuItem key="writer" value="writer">
                                  Editor
                                </MenuItem>
                                <MenuItem
                                  key="freeBusyReader"
                                  value="freeBusyReader"
                                >
                                  Free/Busy Viewer
                                </MenuItem>
                                <MenuItem key="owner" value="owner">
                                  Owner
                                </MenuItem>

                                <MenuItem key="reader" value="reader">
                                  Viewer
                                </MenuItem>
                                <MenuItem
                                  key="writerWithoutPrivateAccess"
                                  value="writerWithoutPrivateAccess"
                                >
                                  Make changes (see private events as free/busy)
                                </MenuItem>
                              </TextField>
                              <Tooltip arrow title="Delete">
                                <span>
                                  <IconButton
                                    onClick={() => {
                                      const copy = { ...shares };
                                      delete copy[user as keyof object];
                                      setValue('shares', copy);
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}
